// Story 11.2 — Hook que extrae el `FlowShape` del tab activo del editor.
// Usado por `EditorPage` para alimentar el `FlowDiagram`. La pure-logic
// (`deriveFlowFromObject`) vive en `lib/flow-extraction.ts` para tests sin
// React/Zustand.

import { useEffect, useState } from 'react';
import {
  type DerivedFlowResult,
  flowNameFromPath,
  isFlowPath,
  parseFlowYaml,
} from '@/lib/flow-extraction';
import { useEditorStore } from '@/stores/editor-store';

const EMPTY: DerivedFlowResult = {
  flow: null,
  notAFlow: true,
  error: null,
};

export function useFlowFromActiveTab(): DerivedFlowResult {
  const tabs = useEditorStore((s) => s.tabs);
  const activePath = useEditorStore((s) => s.activePath);
  const [state, setState] = useState<DerivedFlowResult>(EMPTY);

  // P-372 (review 11.2): re-parse SOLO cuando cambia el `baseline` del tab
  // (i.e., última save), no en cada keystroke. Antes, `useEffect([..., tabs])`
  // disparaba un re-parse + re-layout en cada tecla. Dev Notes line 124 dice
  // explícitamente "Re-render solo on-save"; la implementación original no lo
  // cumplía. Usamos `baseline` (no `content`) para fijar el trigger.
  // P-408 (review 11.3): cuando un tab recién carga del backend, `baseline`
  // y `content` son iguales (el reducer setea baseline desde el content
  // inicial). Pero si `baseline === ''` por algún edge case (e.g. file
  // recién creado sin save), caemos a `content` para que el diagrama
  // muestre algo en lugar del placeholder "No se pudo parsear".
  const tab = tabs.find((t) => t.path === activePath);
  const baseline = tab?.baseline || tab?.content || '';

  // El parse depende solo de `activePath` y `baseline`; `content` está
  // intencionalmente fuera de deps para no re-parsear por keystroke (P-372).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-parse solo on save
  useEffect(() => {
    if (!activePath || !isFlowPath(activePath)) {
      setState(EMPTY);
      return;
    }
    if (!tab) {
      setState({ flow: null, notAFlow: false, error: null });
      return;
    }
    const flowName = flowNameFromPath(activePath);
    let cancelled = false;
    // Parse contra `baseline` (último save) — el overlay "outdated" en
    // FlowDiagram comunica al user que sus edits no están reflejados.
    void parseFlowYaml(baseline, flowName).then((res) => {
      if (cancelled) return;
      setState({ flow: res.flow, notAFlow: false, error: res.error });
    });
    return () => {
      cancelled = true;
    };
  }, [activePath, baseline]);

  return state;
}
